/**
 * Smooth scroll to any Y position on the window or inside a DOM element.
 * Duration scales with distance (sqrt formula) so short scrolls stay
 * quick and long scrolls never feel like racing. Same easeInOutQuart
 * curve throughout for consistent feel on every device.
 *
 * Uses behavior: "instant" per-frame to override the CSS
 * scroll-behavior: smooth on html/body.
 *
 * element — optional DOM node to scroll instead of window.
 * Returns a cancel function (call on cleanup).
 */
export function smoothScrollTo(targetY, element = null) {
  const getScrollY = () => (element ? element.scrollTop : window.scrollY);
  const setScrollY = (y) => {
    if (element) {
      element.scrollTop = y;
    } else {
      window.scrollTo({ top: y, behavior: "instant" });
    }
  };

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    setScrollY(targetY);
    return () => {};
  }

  const startY = getScrollY();
  const distance = targetY - startY;
  if (distance === 0) return () => {};

  // Duration scales with distance via square root — short scrolls stay
  // quick, long scrolls take their time without ever feeling like racing.
  const duration = Math.max(525, Math.min(1650, Math.sqrt(Math.abs(distance)) * 30));

  const startTime = performance.now();

  // easeInOutQuart — gentle start, fast middle, long gradual deceleration to stop
  const ease = (t) =>
    t < 0.5 ? 8 * t * t * t * t : 1 - Math.pow(-2 * t + 2, 4) / 2;

  let raf;

  function step(now) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    setScrollY(Math.round(startY + distance * ease(progress)));
    if (progress < 1) {
      raf = requestAnimationFrame(step);
    }
  }

  raf = requestAnimationFrame(step);
  return () => cancelAnimationFrame(raf);
}
